import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import DateFnsUtils from '@date-io/date-fns';
import { addDays, addYears, isValid as isValidDate, startOfDay } from 'date-fns';
import { Container } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { ThemeProvider } from '@material-ui/styles';
import { Formik, FormikErrors } from 'formik';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import PageHeader, { useStyles } from '../../components/PageHeader';
import { useToggle } from '../../hooks/useToggle';
import { getInventoryEmailEnabled } from '../../services/fetchServices/InventoryApi/getInventoryEmailEnabled';
import {
  emptySendStartInventoryEmailFormValues,
  InventoryStartHttpError,
  sendStartInventoryEmail,
  SendStartInventoryEmailFormValues,
} from '../../services/fetchServices/InventoryApi/sendStartInventoryEmail';
import { calendarTheme } from '../../themes/CalendarTheme';

const getMinDeadline = () => startOfDay(addDays(new Date(), 1));
// The backend rejects a deadline more than a year ahead.
const getMaxDeadline = () => startOfDay(addYears(new Date(), 1));

const ConfirmStartingInventoryDialogContent = ({
  deadline,
  setDeadline,
  error,
}: {
  deadline: Date | null;
  setDeadline: (date: Date | null) => void;
  error?: string;
}) => (
  <Container>
    <div>
      Start a new inventory cycle? This clears hardware checks, inventory comments and
      user reviews, then attempts to notify eligible users. Do not use this action to
      resend notifications.
    </div>
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <ThemeProvider theme={calendarTheme}>
        <KeyboardDatePicker
          disableToolbar
          variant="inline"
          format="dd/MM/yyyy"
          margin="normal"
          id="deadline"
          name="deadline"
          label="deadline"
          value={deadline}
          onChange={setDeadline}
          minDate={getMinDeadline()}
          maxDate={getMaxDeadline()}
          error={!!error}
          helperText={error}
          KeyboardButtonProps={{
            'aria-label': 'change date',
          }}
        />
      </ThemeProvider>
    </MuiPickersUtilsProvider>
  </Container>
);

const InventoryListHeader = ({ onRefresh }: { onRefresh: () => void }) => {
  const classes = useStyles();
  const { toggle, value: isDialogOpen } = useToggle(false);
  const { TEST_ENV } = window._env_;

  const [buttonEnabled, setButtonEnabled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState('');
  const attempted = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      const res = await getInventoryEmailEnabled();
      if (res.type === 'success') {
        setButtonEnabled(TEST_ENV === 'true' || res.data);
      } else {
        toast.error('Cannot fetch information if email sending is enabled');
      }
    };
    fetchData();
  }, [TEST_ENV]);

  const sendEmail = async (values: SendStartInventoryEmailFormValues) => {
    if (values.deadline == null || !isValidDate(values.deadline) || attempted.current)
      return;
    attempted.current = true;
    setButtonEnabled(false);
    try {
      setSubmitting(true);
      const { notifications } = await sendStartInventoryEmail(values);
      const reset = 'Inventory started. Hardware checks and user reviews were reset.';
      setOutcome(
        notifications.unconfirmedCount > 0
          ? `${reset} The mail server accepted ${notifications.acceptedCount} of ${notifications.recipientCount} notifications; ${notifications.unconfirmedCount} are unconfirmed or were not attempted. Contact support to check delivery. Do not start inventory again to resend email.`
          : `${reset} The mail server accepted all ${notifications.acceptedCount} notifications.`,
      );
    } catch (e) {
      const errorDetails =
        e instanceof InventoryStartHttpError
          ? ` (HTTP ${e.status})`
          : ' (network error or unexpected response)';
      // 400 and 415 come from request validation, which runs before the reset.
      const rejected =
        e instanceof InventoryStartHttpError && [400, 415].includes(e.status);
      const denied =
        e instanceof InventoryStartHttpError && [401, 403].includes(e.status);
      if (rejected || denied) {
        attempted.current = false;
        setButtonEnabled(true);
      }
      let message = `Inventory start could not be confirmed${errorDetails}. The reset and some email sends may already have completed. Do not retry: starting again can erase new submissions and resend email. Refresh the list and contact support to verify the outcome.`;
      if (denied) {
        message =
          'Inventory was not started: access was denied. Sign in again with an account allowed to start inventory.';
      } else if (rejected) {
        message = `Inventory was not started: the request was rejected${errorDetails}. Check the deadline and start again.`;
      }
      setOutcome(message);
    } finally {
      setSubmitting(false);
      toggle();
    }
  };
  return (
    <>
      <PageHeader
        title="Inventory"
        actionButtons={
          <Button
            className={classes.button}
            variant="contained"
            color="primary"
            onClick={toggle}
            disabled={!buttonEnabled || submitting || attempted.current}
          >
            START INVENTORY
          </Button>
        }
      />
      <div role="status" aria-live="polite" aria-atomic="true">
        {outcome && (
          <>
            <p>{outcome}</p>
            <Button onClick={onRefresh}>Refresh inventory list</Button>
          </>
        )}
      </div>
      {isDialogOpen && (
        <Formik
          initialValues={emptySendStartInventoryEmailFormValues}
          onSubmit={sendEmail}
          validate={validateSendStartInventoryEmail}
        >
          {({ errors, isValid, setFieldValue, submitForm, values }) => (
            <ConfirmDialog
              content={
                <ConfirmStartingInventoryDialogContent
                  deadline={values.deadline}
                  setDeadline={(deadline) => setFieldValue('deadline', deadline)}
                  error={errors.deadline}
                />
              }
              contentComponent="div"
              title="START INVENTORY"
              onClose={() => {
                if (!submitting) toggle();
              }}
              handleClick={submitForm}
              open={isDialogOpen}
              submitting={submitting}
              submitDisabled={!isValid}
            />
          )}
        </Formik>
      )}
    </>
  );
};

export default InventoryListHeader;

export function validateSendStartInventoryEmail(
  values: SendStartInventoryEmailFormValues,
) {
  const errors: FormikErrors<SendStartInventoryEmailFormValues> = {};
  if (!values.deadline) {
    errors.deadline = 'required';
  } else if (!isValidDate(values.deadline)) {
    errors.deadline = 'invalid date';
  } else if (startOfDay(values.deadline) < getMinDeadline()) {
    errors.deadline = 'must be after today';
  } else if (startOfDay(values.deadline) > getMaxDeadline()) {
    errors.deadline = 'must be within a year';
  }
  return errors;
}
